import Vue from 'vue';
import { cloneDeep, isEqual, remove } from "lodash-es";
import uuidv4 from "../../lib/utils/uuidv4";
import { VNodeFactory } from './VNodeFactory';
import { VNodeFactoryContext } from './VNodeFactoryContext';
import { getCompPathOf, getVNodeFactory } from './utils';
import { StyleLoader } from './StyleLoader';
import { arrayify, getVariableName, walkLayout } from "../../lib/utils";
const completeLayoutIds = (writableLayouts, prefix = '') => {
  const existingIds = new Set();
  walkLayout(writableLayouts, ({
    layout
  }) => {
    if (!Object.isExtensible(layout)) return;
    const oldId = layout.componentId;
    const newId = getVariableName(prefix + ((oldId === null || oldId === void 0 ? void 0 : oldId.replace(/\d+$/, '')) || layout.type), existingIds);
    existingIds.add(newId);
    layout.componentId = newId;
  });
};
/**
 * 如果你的组件里，希望可以输入一个 UICore.Layout 后直接渲染出来，
 * **并且复用当前页面片的上下文和各种变量**，
 * 那么可以使用这个工具
 *
 * 具体方式：在你的组件里：
 *
 * 1. 在 script 里 `inject: { renderer: { from: 'uicore:renderer' } }`
 * 2. 在 template 里 `<component is="renderer.ucPartialLayoutRenderer" :layout="你的那个布局JSON" />`
 */
export function makePartialLayoutRendererComponent(renderer) {
  const {
    uc
  } = renderer;
  return Vue.extend({
    name: 'UcPartialLayoutRenderer',
    props: {
      layout: {
        type: [Object, Array],
        required: true
      }
    },
    data() {
      return {
        vNodeFactoryVersion: 0
      };
    },
    created() {
      const factoryParentCompPath = getCompPathOf(renderer, this);
      const factoryParent = typeof factoryParentCompPath === 'string' ? getVNodeFactory(renderer, factoryParentCompPath) : null;
      const factoryParams = {
        uc,
        comp: {
          type: 'w-container'
        },
        compPath: this.actualPrefix,
        parent: factoryParent,
        isStyleRoot: true,
        isPartialRendererRoot: true
      };
      const getParentVNodeChildren = () => {
        var _this$renderer$ucVNod;
        return (_this$renderer$ucVNod = this.renderer.ucVNodeFactory) === null || _this$renderer$ucVNod === void 0 ? void 0 : _this$renderer$ucVNod.children;
      };
      /** 当前的vNodeFactory是PartialRenderer里的根组件 挂载到父级VNodeFactory后 会被ucFactoryLUT收集 * 从而在byId byCompPath中找到 */
      const pushVNodeFactory = () => {
        var _getParentVNodeChildr;
        (_getParentVNodeChildr = getParentVNodeChildren()) === null || _getParentVNodeChildr === void 0 || _getParentVNodeChildr.push(this.vNodeFactory);
      };
      const popVNodeFactory = () => {
        const childrenArray = getParentVNodeChildren();
        if (childrenArray) {
          remove(childrenArray, item => item === this.vNodeFactory);
        }
      };
      this.$once('hook:beforeDestroy', popVNodeFactory);
      const rebuildVNodeFactory = () => {
        popVNodeFactory();
        this.vNodeFactory = new VNodeFactory(factoryParams);
        pushVNodeFactory();
        this.vNodeFactoryVersion += 1;
      };
      this.$watch(() => this.actualPrefix, prefix => {
        factoryParams.compPath = prefix;
        rebuildVNodeFactory();
      });
      let internalLayout;
      this.$watch(() => arrayify(this.$props.layout), newLayout => {
        // 给到factoryParams.comp的是补充了componentId的 所以要缓存一份internalLayout做之后的diff
        if (isEqual(internalLayout, newLayout)) return;
        internalLayout = cloneDeep(newLayout); // TODO: 这里改成存对象hash会节约点内存吧
        const comp = {
          type: 'w-container',
          children: cloneDeep(internalLayout)
        };
        completeLayoutIds(comp, this.uuid);
        factoryParams.comp = comp;
        rebuildVNodeFactory();
      }, {
        deep: true,
        immediate: true
      });
      // style loader
      this.ucStyleLoader = new StyleLoader(this.renderer, {
        getPagelet: () => null,
        getVNodeFactory: () => (this.vNodeFactoryVersion, this.vNodeFactory)
      });
    },
    methods: {
      getChildrenVNodes() {
        var _vNodeFactory$childre;
        const {
          vNodeFactory
        } = this;
        //  给 Vue 依赖收集用的
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.vNodeFactoryVersion;
        this.VNodeFactoryContext.depend();
        const vNodes = vNodeFactory === null || vNodeFactory === void 0 || (_vNodeFactory$childre = vNodeFactory.children) === null || _vNodeFactory$childre === void 0 ? void 0 : _vNodeFactory$childre.map(child => child.toVNode(this.VNodeFactoryContext));
        return (vNodes === null || vNodes === void 0 ? void 0 : vNodes.filter(Boolean)) || [];
      }
    },
    beforeMount() {
      this.ucStyleLoader.init();
    },
    beforeDestroy() {
      this.ucStyleLoader.dispose();
    },
    computed: {
      renderer() {
        return renderer;
      },
      ownerPrefix() {
        const ownerPrefix = getCompPathOf(this.renderer, this) || '';
        return ownerPrefix;
      },
      actualPrefix() {
        return `${this.ownerPrefix}/temp-${this.uuid}/0`;
      },
      uuid() {
        return uuidv4();
      },
      VNodeFactoryContext() {
        const ctx = new VNodeFactoryContext(renderer);
        ctx.isPartialRenderer = true;
        return ctx;
      }
    },
    render() {
      const h = arguments[0];
      const vNodes = this.getChildrenVNodes();
      if (!(vNodes !== null && vNodes !== void 0 && vNodes.length)) return this.$createElement();
      if (vNodes.length === 1) return vNodes[0];
      return h("div", {
        "style": "display:contents",
        "attrs": {
          "data-is-partial-renderer": true
        }
      }, [vNodes]);
    }
  });
}
//# sourceMappingURL=PartialLayoutRenderer.js.map
